import { useEffect } from "react";
import { Navigate, Outlet } from "react-router";
import { useAuthStore } from "../stores/authStore";
import {
  isEnterpriseSubdomain, isAppSubdomain,
  appSubdomainUrl, enterpriseSubdomainUrl,
} from "../lib/subdomain";

/**
 * ProtectedRoute — guards all authenticated pages and enforces subdomain placement.
 *
 * Unauthenticated redirect rules:
 *   - On app subdomain     → /login  (relative, stays on app.tsnet.dev)
 *   - On enterprise sub    → app.tsnet.dev/login  (absolute, always)
 *     Reason: the enterprise subdomain /login page has no way to handle
 *     a cold-start — there's no session, no handoff token. The only
 *     recovery path is the app subdomain login.
 *
 * Subdomain enforcement (authenticated):
 *   - Platform admin on enterprise sub → bounced to app subdomain
 *   - Enterprise user on app sub       → bounced to enterprise subdomain
 */
export function ProtectedRoute() {
  const { accessToken, user } = useAuthStore();

  useEffect(() => {
    if (!user) return;
    const isPlatformUser =
      user.platformRole === "platform_admin" || user.platformRole === "support";
    const userDefaultSlug = (
      user as unknown as { defaultEnterpriseSlug?: string }
    ).defaultEnterpriseSlug;

    if (isPlatformUser && isEnterpriseSubdomain()) {
      const url = appSubdomainUrl("/dashboard");
      if (url.startsWith("http")) window.location.href = url;
    } else if (!isPlatformUser && userDefaultSlug && isAppSubdomain()) {
      const url = enterpriseSubdomainUrl(userDefaultSlug, "/dashboard");
      if (url.startsWith("http")) window.location.href = url;
    }
  }, [user]);

  if (!accessToken) {
    // On enterprise subdomain — always send back to app subdomain login.
    // A relative /login here would land on acme.tsnet.dev/login which
    // has no session context and would show a confusing blank form.
    if (isEnterpriseSubdomain()) {
      const loginUrl = appSubdomainUrl("/login");
      if (loginUrl.startsWith("http")) {
        window.location.replace(loginUrl);
        return null; // prevent any render while redirect fires
      }
    }
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
